import React, { useState, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { Slide, toast } from "react-toastify";
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import { styled } from "@mui/system";

export const ModalUploadRetailerTemp = ({
  open,
  onClose,
  onSuccess,
  uploadRetailerId,
  currentPeriod,
}) => {
  const CancelButton = styled(Button)({
    backgroundColor: "#DE3436",
    color: "white",
    "&:hover": {
      backgroundColor: "#DC1F21",
    },
  });

  const ConfirmButton = styled(Button)({
    backgroundColor: "#469F3F",
    color: "white",
    "&:hover": {
      backgroundColor: "#32932B",
    },
  });

  const accessToken = Cookies.get("access_token") || "";

  const [dataRetailer, setDataRetailer] = useState([]);
  const [selectedRetailer, setSelectedRetailer] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [file, setFile] = useState(null);
  const [showExistConfirm, setShowExistConfirm] = useState(false);

  const optionsWithAll = [
    { id: "all-retailers", retailer: "All Retailers" },
    ...dataRetailer,
  ];

  async function fetchRetailer() {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/data-processing-raw/rta_mt/retailer`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        const { data } = await response.json();
        setDataRetailer(data);
      } else {
        setDataRetailer([]);
      }
    } catch (error) {
      console.error(error);
    }
  }

  const handleDownloadTemplate = async () => {
    if (selectedPeriod) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/v1/data-processing-raw/rta_mt/template/retailer/export`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        if (response.status === 200) {
          const blob = await response.blob();
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = `${selectedRetailer}_${selectedPeriod}.xlsx`;
          link.click();
        } else {
          console.error("Failed to download the template.");
        }
      } catch (error) {
        console.error(error);
      }
    } else {
      toast.error("Harap mengisi period terlebih dahulu");
    }
  };

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleUpload = async () => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("retailer", selectedRetailer);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/data-processing-single/data_preparation/retailer_data/upload/single/rta_mt`,
        {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.status === 201) {
        const data = await response.json();
        toast.success(data.detail);
        if (onSuccess) {
          onSuccess();
        }
        handleClose();
      } else if (response.status === 400) {
        const errorMessage = await response.json();
        toast.error(
          `Failed to upload the file: ${
            errorMessage.detail || "Error details not available"
          }`
        );
      } else {
        console.error("Failed to upload the file.");
        toast.error("Failed to upload the file.");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while uploading the file.");
    } finally {
      setShowExistConfirm(false);
    }
  };

  const handleConfirmClick = async () => {
    if (!file || !selectedRetailer || !selectedPeriod) {
      toast.error("Please select a retailer, period, and then upload a file.");
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/data-processing-single/data_preparation/retailer_data/check/rta_mt?retailer=${selectedRetailer}&period=${selectedPeriod}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.exist_data) {
          setShowExistConfirm(true);
        } else {
          handleUpload();
        }
      } else {
        toast.error("Failed to check for existing data.");
      }
    } catch (error) {
      console.error("Error checking for existing data:", error);
      toast.error("An error occurred while checking for existing data.");
    }
  };

  const handleClose = () => {
    setSelectedRetailer("");
    setSelectedPeriod("");
    setFile(null);
    setShowExistConfirm(false);
    onClose();
  };

  const handleRetailerChange = (event) => {
    setSelectedRetailer(event.target.value);
  };

  const handlePeriodChange = (event) => {
    setSelectedPeriod(event.target.value);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchRetailer();
    }, 300);
    return () => {
      clearTimeout(handler);
    };
  }, []);

  useEffect(() => {
    if (dataRetailer && uploadRetailerId) {
      setSelectedRetailer(uploadRetailerId);
    }
  }, [open, dataRetailer, uploadRetailerId]);

  useEffect(() => {
    if (currentPeriod) {
      setSelectedPeriod(currentPeriod);
    }
  }, [open, currentPeriod]);

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            backgroundColor: "white",
            border: "0px solid #000",
            boxShadow: 24,
            padding: "16px 32px 24px",
            borderRadius: "5px",
          }}
        >
          <Typography
            sx={{ mt: 2, fontSize: "18px", fontWeight: "600", color: "#383838" }}
          >
            Upload Retailer Data
          </Typography>
          <Autocomplete
            size="small"
            options={optionsWithAll || []}
            getOptionLabel={(item) => item.retailer || ""}
            value={
              optionsWithAll?.find(
                (item) => item.retailer === selectedRetailer
              ) || null
            }
            onChange={(event, newValue) => {
              handleRetailerChange({
                target: { value: newValue ? newValue.retailer : "" },
              });
            }}
            sx={{ mt: 2 }}
            disabled
            renderInput={(params) => (
              <TextField
                {...params}
                label="Retailer"
                className="reporting-textfield"
                sx={{ minWidth: 400 }}
              />
            )}
            isOptionEqualToValue={(item, value) =>
              item.retailer === value.retailer
            }
            disableClearable
          />
          <TextField
            id="outlined-select-period"
            label="Period"
            sx={{ width: '100%', mt: 2 }}
            size="small"
            value={selectedPeriod}
            onChange={handlePeriodChange}
            className="reporting-textfield"
            disabled={!!currentPeriod}
          />

          <Box
            mt={4}
            p={2}
            sx={{
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              gap={2}
            >
              <Typography variant="body1" sx={{ fontWeight: "500" }}>
                Pilih file excel untuk di upload:
              </Typography>

              {file && (
                <Typography variant="body2" sx={{ mt: 1, wordBreak: 'break-all' }}>
                  {file.name}
                </Typography>
              )}

              <Button
                variant="outlined"
                component="label"
                sx={{
                  borderColor: "#1976d2",
                  color: "#1976d2",
                  width: "100%",
                  "&:hover": {
                    borderColor: "#115293",
                    backgroundColor: "rgba(25, 118, 210, 0.1)",
                  },
                }}
              >
                Choose File
                <input
                  type="file"
                  accept=".xlsx"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>

              <Button
                onClick={handleDownloadTemplate}
                variant="outlined"
                component="label"
                sx={{
                  borderColor: "#1976d2",
                  color: "#1976d2",
                  width: "100%",
                  "&:hover": {
                    borderColor: "#115293",
                    backgroundColor: "rgba(25, 118, 210, 0.1)",
                  },
                }}
              >
                Download Excel Template
              </Button>
            </Box>
          </Box>

          <Box mt={4} display="flex" justifyContent="flex-end">
            <CancelButton onClick={handleClose} style={{ marginRight: "8px" }}>
              Tidak
            </CancelButton>
            <ConfirmButton onClick={handleConfirmClick}>Ya</ConfirmButton>
          </Box>
        </Box>
      </Modal>

      <Modal open={showExistConfirm} onClose={() => setShowExistConfirm(false)}>
        <Box
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            backgroundColor: "white",
            border: "0px solid #000",
            boxShadow: 24,
            padding: "16px 32px 24px",
            borderRadius: "5px",
          }}
        >
          <Typography
            sx={{
              mt: 2,
              mb: 2,
              fontSize: "18px",
              fontWeight: "600",
              color: "#383838",
            }}
          >
            {selectedRetailer} - {selectedPeriod}
          </Typography>
          <Typography>
            Ditemukan data {selectedRetailer} yang telah diunggah sebelumnya untuk periode ini. Apakah Anda ingin memperbarui data tersebut?
          </Typography>
          <Box mt={4} display="flex" justifyContent="flex-end">
            <CancelButton
              onClick={() => setShowExistConfirm(false)}
              style={{ marginRight: "8px" }}
            >
              No
            </CancelButton>
            <ConfirmButton onClick={handleUpload}>Yes</ConfirmButton>
          </Box>
        </Box>
      </Modal>
    </>
  );
};